import Doc, { IDocQueryParams } from "models/document";
import { Dispatch } from "redux";
import { setEmployeeCount, setEmployees, setEmployeeSearchFields, setIsLoadingEmployees } from "./reducer";
import { toast } from "react-toastify";
import Employee, { IEmployeeQueryParams } from "models/employee";
import { PaginatedResult } from "models/queryable";
import ConsultantFields from "Components/organisms/forms/contractor-form/types";


export const getEmployees = async (query?: IEmployeeQueryParams): Promise<PaginatedResult<Employee> | null> => {
    try {
        let queryString = '';
        if (query) {
            const params = new URLSearchParams();

            Object.entries(query).forEach(([key, value]) => {
                if (value === undefined || value === null || value === '') return;

                if (Array.isArray(value)) {
                    value.forEach(v => {
                        if (v) params.append(key, v);
                    });
                } else {
                    params.append(key, String(value));
                }
            });

            queryString = params.toString();
        }

        const url = `${process.env.REACT_APP_API_URL}/employee${queryString ? `?${queryString}` : ''}`;
        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        })

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const emps = await res.json();

        return emps.data as PaginatedResult<Employee>
    } catch (err: any) {
        toast.error(err.message ?? "Error getting employees");
        return null
    }
}


export const getEmployee = async (empId: string): Promise<Employee | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/employee/${empId}`;
        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        })

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data as Employee
    } catch (err: any) {
        toast.error(err.message ?? "Error getting employees");
        return null
    }
}


export const getEmployeeFormFieldValues = () => async (dispatch: Dispatch) => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/employee/search-form-fields`

        const res = await fetch(url, {
            credentials: 'include',
        })

        if (!res.ok) {
            throw new Error("HTTP Error");
        }


        const fieldValues = await res.json();

        dispatch(setEmployeeSearchFields(fieldValues.data))
        return fieldValues.data;
    } catch (err: any) {
        toast.error("Error getting Consultants Form Field Values");
        console.error("Error getting Consultants Form Field Values :", err);
    }
}

export const downloadEmployeesCsv = async (query?: IEmployeeQueryParams): Promise<void> => {
    try {
        let queryString = '';
        if (query) {
            const params = new URLSearchParams();
            Object.entries(query).forEach(([key, value]) => {
                if (value === undefined || value === null || value === '') return;
                if (Array.isArray(value)) {
                    value.forEach(v => { if (v) params.append(key, v); });
                } else {
                    params.append(key, String(value));
                }
            });
            queryString = params.toString();
        }

        const url = `${process.env.REACT_APP_API_URL}/employee/export${queryString ? `?${queryString}` : ''}`;
        const res = await fetch(url, { credentials: 'include', method: 'GET' });

        if (!res.ok) {
            const errBody = await res.json().catch(() => null);
            throw new Error(errBody?.message ?? 'Failed to export CSV');
        }

        const blob = await res.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = 'employees.csv';
        a.click();
        window.URL.revokeObjectURL(blobUrl);

    } catch (err: any) {
        toast.error(err.message ?? 'Error downloading CSV');
    }
};

export const submitConsultantForm = async (vals: ConsultantFields, handleResetForm: () => any): Promise<Employee | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/employee/consultants`

        // Make sure to remove employee type. That is set by the api
        const payload: any = { ...vals };
        Object.keys(payload).forEach(k => {
            if (payload[k] === "" || k === "employeeTypeId")
                payload[k] = undefined;
        })

        const res = await fetch(url, {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
                'Content-Type': 'application/json',
            }
        })

        if (!res.ok) {
            const errBody = await res.json()
            throw new Error(errBody.message);
        }

        const { data } = await res.json();
        handleResetForm()
        toast.success("Consultatnt Created.")
        return data
    } catch (err: any) {
        toast.error(err.message ?? "Error submitting Consultant form");
        return null;
    }
}

export const submitPotentialConsultantForm = async (vals: ConsultantFields, handleResetForm: () => any): Promise<Employee | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/employee/potential-consultants`

        // Make sure to remove employee type. That is set by the api
        const payload: any = { ...vals };
        Object.keys(payload).forEach(k => {
            if (payload[k] === "" || k === "employeeTypeId")
                payload[k] = undefined;
        })

        const res = await fetch(url, {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
                'Content-Type': 'application/json',
            }
        })

        if (!res.ok) {
            const errBody = await res.json()
            throw new Error(errBody.message);
        }

        const { data } = await res.json();
        handleResetForm()
        toast.success("Consultatnt Created.")
        return data
    } catch (err: any) {
        toast.error(err.message ?? "Error submitting Consultant form");
        return null;
    }
}

export const submitEditConsultantForm = async (consultantId: string, vals: ConsultantFields, handleResetForm: () => any): Promise<Employee | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/employee/consultants/${consultantId}`

        // Make sure to remove employee type. That is set by the api
        const payload: any = { ...vals };
        Object.keys(payload).forEach(k => {
            if (payload[k] === "" || k === "employeeTypeId")
                payload[k] = undefined;
        })

        const res = await fetch(url, {
            credentials: 'include',
            method: 'PUT',
            body: JSON.stringify(payload),
            headers: {
                'Content-Type': 'application/json',
            }
        })

        if (!res.ok) {
            const errBody = await res.json()
            throw new Error(errBody.message);
        }

        const { data } = await res.json();
        handleResetForm()
        toast.success("Consultant edited.")
        return data;
    } catch (err: any) {
        toast.error(err.message ?? "Error submitting edit consultant form");
        return null;
    }
}

export const submitEditPotentialConsultantForm = async (potentialConsultantId: string, vals: ConsultantFields, handleResetForm: () => any): Promise<Employee | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/employee/potential-consultants/${potentialConsultantId}`

        // Make sure to remove employee type. That is set by the api
        const payload: any = { ...vals };
        Object.keys(payload).forEach(k => {
            if (payload[k] === "" || k === "employeeTypeId")
                payload[k] = undefined;
        })

        const res = await fetch(url, {
            credentials: 'include',
            method: 'PUT',
            body: JSON.stringify(payload),
            headers: {
                'Content-Type': 'application/json',
            }
        })

        if (!res.ok) {
            const errBody = await res.json()
            throw new Error(errBody.message);
        }

        const { data } = await res.json();
        handleResetForm()
        toast.success("Consultant edited.")
        return data;
    } catch (err: any) {
        toast.error(err.message ?? "Error submitting edit consultant form");
        return null;
    }
}

export const deleteConsultant = async (consultant: Employee): Promise<boolean> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/employee/consultants/${consultant.id}`

        const res = await fetch(url, {
            credentials: 'include',
            method: "DELETE"
        })

        if (!res.ok) {
            const data = await res.json();
            toast.error(data.message ?? "Error deleting consultant")
            return false;
        }

        toast.success("Consultant deleted.")
        return true
    } catch (err: any) {
        toast.error("Error deleting consultant");
        console.error("Error deleting consultant:", err);
        return false
    }
}

export const updateEmployeeWillingToRelocate = async (empId: String, value: boolean): Promise<boolean> => {
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/employee/willing-to-relocate/${empId}`, {
            credentials: 'include',
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                willingToRelocate: value
            })
        })

        if (res.ok) {
            toast.success("Profile updated.")
            return true
        }

        const data = await res.json()
        toast.error(data.message ?? "Failed to update profile.")
        return false
    } catch (err: any) {
        toast.error("Something went wrong.")
        return false
    }
}

export const updateEmployeeEsaExempt = async (empId: String, value: boolean): Promise<boolean> => {
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/employee/esa-exempt/${empId}`, {
            credentials: 'include',
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                esaExempt: value
            })
        })

        if (res.ok) {
            toast.success("Employee updated.")
            return true
        }

        const data = await res.json()
        toast.error(data.message ?? "Failed to update employee.")
        return false
    } catch (err: any) {
        toast.error("Something went wrong.")
        return false
    }
}

export const updateEmployeeContractEndDate =async (empId: String, value: string | null): Promise<boolean> => {
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/employee/contract-end-date/${empId}`, {
            credentials: 'include',
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                contractEndDate: value
            })
        })

        if (res.ok) {
            toast.success("Contract end date updated.")
            return true
        }

        const data = await res.json()
        toast.error(data.message ?? "Failed to update contract end date.")
        return false
    } catch (err: any) {
        toast.error("Something went wrong.")
        return false
    }
}


